/**
 * Word document operations (Office.js Word API).
 * All functions assume Office.onReady has resolved in a Word host.
 *
 * Write operations take HTML (callers convert the model's Markdown via
 * `markdownToHtml`) so applied content keeps headings, bold, lists, tables.
 */

export async function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body
    body.load('text')
    await context.sync()
    return body.text
  })
}

/** Paragraph texts in document order; indexes match `replaceParagraphRange`. */
export async function getDocumentParagraphs(): Promise<string[]> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs
    paragraphs.load('text')
    await context.sync()
    return paragraphs.items.map((paragraph) => paragraph.text)
  })
}

export interface DocumentParagraph {
  text: string
  /** Built-in style name (e.g. "Heading1", "Title", "Normal", "Other"). */
  style?: string
  /** Table nesting depth: 0 = body text, 1+ = text inside a table cell. */
  tableNestingLevel?: number
  /**
   * Exact rendered list marker ("1.", "a)", "•") when the paragraph is a list
   * item. Numbered and bulleted lists BOTH carry style "ListParagraph", so the
   * marker is the only way to tell the list type apart.
   */
  listMarker?: string
  listLevel?: number
  /** Paragraph alignment when not default left. */
  alignment?: 'center' | 'right' | 'justify'
  /** Set when the ENTIRE paragraph is uniformly formatted (mixed runs read null). */
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

const ALIGNMENT_MAP: Record<string, DocumentParagraph['alignment']> = {
  Centered: 'center',
  Justified: 'justify',
  Right: 'right'
}

/**
 * Paragraphs with built-in style names and table depth, for the document
 * mirror and outline. `body.paragraphs` flattens table-cell paragraphs into
 * the sequence (losing row/column structure), so the nesting level is the
 * signal that lets the agent avoid edits that would cut across a table.
 * Both properties need WordApi 1.3; if the host rejects them, degrade to
 * text-only rather than losing the document read entirely.
 */
export async function getDocumentParagraphsDetailed(): Promise<DocumentParagraph[]> {
  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs
      paragraphs.load(
        'text,styleBuiltIn,style,alignment,tableNestingLevel,' +
          'font/bold,font/italic,font/underline,' +
          'listItemOrNullObject/listString,listItemOrNullObject/level'
      )
      await context.sync()
      return paragraphs.items.map((paragraph) => {
        const listItem = paragraph.listItemOrNullObject
        const inList = listItem && !listItem.isNullObject
        const alignment = ALIGNMENT_MAP[paragraph.alignment]
        const { bold, italic, underline } = paragraph.font
        return {
          text: paragraph.text,
          // styleBuiltIn says just "Other" for custom styles; surface the name.
          style: paragraph.styleBuiltIn === 'Other' ? paragraph.style : paragraph.styleBuiltIn,
          tableNestingLevel: paragraph.tableNestingLevel,
          ...(inList ? { listMarker: listItem.listString, listLevel: listItem.level } : {}),
          ...(alignment ? { alignment } : {}),
          ...(bold === true ? { bold: true } : {}),
          ...(italic === true ? { italic: true } : {}),
          ...(underline && underline !== 'None' ? { underline: true } : {})
        }
      })
    })
  } catch {
    const texts = await getDocumentParagraphs()
    return texts.map((text) => ({ text }))
  }
}

export async function getSelection(): Promise<string> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()
    selection.load('text')
    await context.sync()
    return selection.text
  })
}

export interface SelectedParagraphRange {
  start: number
  end: number
  expectedStartText: string
  expectedEndText: string
}

/** Resolve the live selection to body-paragraph indexes for the manual tool
 * shelf. Range comparison disambiguates repeated policy wording safely. */
export async function getSelectedParagraphRange(): Promise<SelectedParagraphRange> {
  return Word.run(async (context) => {
    const all = context.document.body.paragraphs
    const selected = context.document.getSelection().paragraphs
    all.load('items,text')
    selected.load('items,text')
    await context.sync()
    if (selected.items.length === 0) throw new Error('Select one or more Word paragraphs first.')

    const resolveIndex = async (selectedParagraph: Word.Paragraph): Promise<number> => {
      const candidates = all.items
        .map((paragraph, index) => ({ paragraph, index }))
        .filter(({ paragraph }) => paragraph.text === selectedParagraph.text)
      const selectedRange = selectedParagraph.getRange(Word.RangeLocation.whole)
      const relations = candidates.map(({ paragraph }) =>
        paragraph.getRange(Word.RangeLocation.whole).compareLocationWith(selectedRange)
      )
      await context.sync()
      const match = candidates.find((_, index) => relations[index].value === Word.LocationRelation.equal)
      if (!match) throw new Error('Could not map the Word selection to a document paragraph.')
      return match.index
    }

    const start = await resolveIndex(selected.items[0])
    const end = await resolveIndex(selected.items[selected.items.length - 1])
    return {
      start,
      end,
      expectedStartText: all.items[start].text,
      expectedEndText: all.items[end].text
    }
  })
}

/** Base character formatting inherited from the text surrounding an insertion. */
interface BaseFont {
  name: string | null
  size: number | null
  color: string | null
}

/** Word reads font.color as "#RRGGBB"; anything else (null/mixed/automatic) is not re-applied. */
function inheritableColor(color: string | null): string | undefined {
  return color && /^#[0-9a-fA-F]{6}$/.test(color) ? color : undefined
}

/** Heading-styled paragraphs keep their Word style's look instead of the base font. */
function keepsHeadingStyle(paragraph: Word.Paragraph): boolean {
  const style = paragraph.styleBuiltIn
  return typeof style === 'string' && /^(Heading\d|Title|Subtitle)/.test(style)
}

/**
 * Make HTML-inserted content adopt the surrounding document's base font.
 * `insertHtml` ignores the anchor's formatting: new content lands in Word's
 * HTML-import defaults (family, size, AND color), which almost never match the
 * document. For markup without headings the base font is applied to the whole
 * inserted range in the same request batch (safe for inline spans, and nothing
 * half-commits if Word rejects an op). Heading-carrying markup maps <h1>..<h6>
 * onto the document's own Heading styles, whose look must win — there the base
 * font is applied per paragraph, skipping the heading-styled ones. (Previously
 * heading-carrying insertions skipped inheritance entirely, so every appended
 * "## Heading + body" provision kept HTML-default fonts in its body text.)
 * When the anchor spans mixed fonts, name/size/color read back null and that
 * property is left as Word produced it rather than forcing an arbitrary value.
 */
async function applyBaseFontToInsertedHtml(
  context: Word.RequestContext,
  inserted: Word.Range,
  html: string,
  base: BaseFont
): Promise<void> {
  const name = base.name || undefined
  const size = typeof base.size === 'number' && base.size > 0 ? base.size : undefined
  const color = inheritableColor(base.color)
  if (!name && size === undefined && !color) return
  if (!/<h[1-6](?:\s|>)/i.test(html)) {
    if (name) inserted.font.name = name
    if (size !== undefined) inserted.font.size = size
    if (color) inserted.font.color = color
    return
  }
  const paragraphs = inserted.paragraphs
  paragraphs.load('items/styleBuiltIn')
  await context.sync()
  for (const paragraph of paragraphs.items) {
    if (keepsHeadingStyle(paragraph)) continue
    if (name) paragraph.font.name = name
    if (size !== undefined) paragraph.font.size = size
    if (color) paragraph.font.color = color
  }
}

/**
 * Insert HTML and make it adopt the document's font instead of Word's
 * HTML-import defaults (see applyBaseFontToInsertedHtml). Inline bold/italic
 * from the markup are independent font properties, so they survive.
 *
 * `anchor` is a range whose font represents the surrounding text — the range
 * being replaced, or a collapsed point next to where we insert.
 */
async function insertHtmlInheritingFont(
  context: Word.RequestContext,
  anchor: Word.Range,
  html: string,
  location: Word.InsertLocation
): Promise<Word.Range> {
  anchor.font.load('name,size,color')
  await context.sync()
  const inserted = anchor.insertHtml(html, location)
  await applyBaseFontToInsertedHtml(context, inserted, html, anchor.font)
  return inserted
}

/**
 * Replace `target`'s text with plain `plainText` while keeping the font SIZE
 * (and family) of the original meaningful content.
 *
 * `insertText(Replace)` makes the new text adopt the character formatting of the
 * FIRST character of the replaced span. That is wrong when the span starts with
 * whitespace at an anomalous size — common in PDF-converted policies where a
 * leading tab carries a different font size than the label it precedes, so
 * removing the tab would resize the whole label. We read the font from the first
 * non-whitespace token of the ORIGINAL text (`referenceText`, e.g. the anchor or
 * expected paragraph text) and re-apply it after the replace. When there is no
 * usable reference token — or it isn't found in the span — we fall back to the
 * span's own font, so the result is never worse than a bare insertText.
 */
async function insertTextPreservingFont(
  context: Word.RequestContext,
  target: Word.Range,
  plainText: string,
  referenceText?: string
): Promise<Word.Range> {
  let name: string | undefined
  let size: number | undefined
  let color: string | null = null
  let captured = false

  const token = referenceText?.trim().split(/\s+/)[0]
  if (token) {
    const matches = target.search(token, SEARCH_OPTIONS)
    matches.load('items')
    await context.sync()
    if (matches.items.length > 0) {
      const font = matches.items[0].font
      font.load('name,size,color')
      await context.sync()
      name = font.name
      size = font.size
      color = font.color
      captured = true
    }
  }
  if (!captured) {
    // No reference token (or not found): fall back to the span's own font, which
    // is correct when the span is uniform and reads null (skipped below) when it
    // is mixed — matching the pre-fix insertText behaviour.
    target.font.load('name,size,color')
    await context.sync()
    name = target.font.name
    size = target.font.size
    color = target.font.color
  }

  const inserted = target.insertText(plainText, Word.InsertLocation.replace)
  if (name) inserted.font.name = name
  if (typeof size === 'number' && size > 0) inserted.font.size = size
  const inheritedColor = inheritableColor(color)
  if (inheritedColor) inserted.font.color = inheritedColor
  return inserted
}

export async function insertHtmlAtSelection(html: string, expectedSelectionText?: string): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()
    if (expectedSelectionText) {
      selection.load('text')
      await context.sync()
      assertExpectedText(selection.text, expectedSelectionText, 'selected text')
    }
    await insertHtmlInheritingFont(context, selection, html, Word.InsertLocation.end)
    await context.sync()
  })
}

/** Append HTML to the body end, inheriting the tail's base font. Shared by the
 * single append tool and the batch apply; callers own the final sync. */
async function appendHtmlWithBaseFont(context: Word.RequestContext, html: string): Promise<Word.Range> {
  const body = context.document.body
  body.load('text')
  // Take the base font from the existing tail (the body's font reads as mixed
  // across a real document) so appended content matches it, not Word's default.
  const lastParagraph = body.paragraphs.getLast()
  lastParagraph.font.load('name,size,color')
  await context.sync()

  const separatedHtml = body.text.trim() ? `<p><br></p>${html}` : html
  const inserted = body.insertHtml(separatedHtml, Word.InsertLocation.end)
  await applyBaseFontToInsertedHtml(context, inserted, separatedHtml, lastParagraph.font)
  return inserted
}

export async function appendHtmlToDocument(html: string): Promise<void> {
  return Word.run(async (context) => {
    await appendHtmlWithBaseFont(context, html)
    await context.sync()
  })
}

export async function replaceSelectionWithHtml(
  html: string,
  expectedSelectionText?: string,
  plainText?: string
): Promise<void> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection()
    if (expectedSelectionText) {
      selection.load('text')
      await context.sync()
      assertExpectedText(selection.text, expectedSelectionText, 'selected text')
    }
    await assertRangeDoesNotCrossTableParagraphs(context, selection)
    // Plain replacements use insertText so the selection's character formatting
    // (bold, font, color) survives; the helper additionally restores the label's
    // font size when the span starts with a differently-sized leading character.
    // HTML replacements inherit the selection's base font so they don't reset to
    // Word's HTML defaults. Empty content deletes the selection (insertHtml
    // rejects empty strings).
    const inserted = await replaceTextTarget(context, selection, html, plainText, expectedSelectionText ?? '')
    // Select the new content so Word scrolls the edit into view.
    inserted.select()
    await context.sync()
  })
}

/** Replace the entire document body. */
export async function replaceDocumentBodyWithHtml(html: string): Promise<void> {
  return Word.run(async (context) => {
    context.document.body.insertHtml(html, Word.InsertLocation.replace)
    await context.sync()
  })
}

/**
 * Replace a contiguous range of paragraphs (zero-based, inclusive). Indexes
 * correspond to the «N» prefixes in the document snapshot, so the model can
 * target whole sections without text anchors or length limits.
 */
function normalizeParagraphText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/**
 * Thrown by the resolve helpers when a proposal's anchor no longer matches the
 * live document (the document changed since the proposal was created) — as
 * opposed to an unexpected Office.js failure. The apply path surfaces its
 * message to the user; the read-only `canResolve*` checks treat it as "stale"
 * while letting genuine errors propagate.
 */
export class AnchorNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AnchorNotFoundError'
  }
}

export class UnsafeWordRangeError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UnsafeWordRangeError'
  }
}

export class AmbiguousWordAnchorError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AmbiguousWordAnchorError'
  }
}

async function assertRangeDoesNotCrossTableParagraphs(
  context: Word.RequestContext,
  range: Word.Range
): Promise<void> {
  const paragraphs = range.paragraphs
  paragraphs.load('items')
  await context.sync()
  if (paragraphs.items.length <= 1) return
  paragraphs.items.forEach((paragraph) => paragraph.load('tableNestingLevel'))
  await context.sync()
  if (paragraphs.items.some((paragraph) => paragraph.tableNestingLevel > 0)) {
    throw new UnsafeWordRangeError(
      'This selection crosses multiple table-cell paragraphs and cannot be replaced safely. ' +
        'Select text inside one paragraph or ask Ramp AI for smaller replacements.'
    )
  }
}

/** Anchors come from full read results. Compare all normalized text so a
 * same-prefix paragraph or a change after the first sentence cannot pass the
 * stale guard. */
function paragraphMatchesAnchor(actual: string, expected: string): boolean {
  const expectedNormalized = normalizeParagraphText(expected)
  return expectedNormalized.length > 0 && normalizeParagraphText(actual) === expectedNormalized
}

function assertExpectedText(actual: string, expected: string, label: string): void {
  if (!paragraphMatchesAnchor(actual, expected)) {
    throw new AnchorNotFoundError(
      `The ${label} changed since this proposal was created. ` +
        'Ask Ramp AI to review the latest document and create a fresh proposal.'
    )
  }
}

/** A resolved paragraph range: the live paragraph list plus the (possibly
 * relocated) inclusive bounds within it, so callers can build either one
 * spanning range or per-paragraph ranges. */
interface ResolvedParagraphRange {
  items: Word.Paragraph[]
  start: number
  end: number
}

async function assertSafeParagraphRange(
  context: Word.RequestContext,
  resolved: ResolvedParagraphRange
): Promise<void> {
  if (resolved.start === resolved.end) return
  const targetParagraphs = resolved.items.slice(resolved.start, resolved.end + 1)
  targetParagraphs.forEach((paragraph) => paragraph.load('tableNestingLevel'))
  await context.sync()
  if (targetParagraphs.some((paragraph) => paragraph.tableNestingLevel > 0)) {
    throw new UnsafeWordRangeError(
      'This replacement spans multiple table-cell paragraphs and cannot be applied safely. ' +
        'Ask Ramp AI to split it into one paragraph or a smaller text replacement.'
    )
  }
}

/** The single range spanning paragraphs `start`..`end` (inclusive). */
function paragraphSpanRange(items: Word.Paragraph[], start: number, end: number): Word.Range {
  return items[start].getRange(Word.RangeLocation.whole).expandTo(items[end].getRange(Word.RangeLocation.whole))
}

/**
 * Resolve a contiguous paragraph range (zero-based, inclusive) to the live
 * paragraph list and (possibly relocated) bounds, validating the indexes and
 * optionally the anchor text. Shared by the replace and scroll-to-target paths.
 */
async function resolveParagraphRange(
  context: Word.RequestContext,
  start: number,
  end: number,
  expectedStartText?: string,
  expectedEndText?: string
): Promise<ResolvedParagraphRange> {
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < start) {
    throw new Error(`Invalid paragraph range ${start}–${end}.`)
  }
  const paragraphs = context.document.body.paragraphs
  paragraphs.load('items')
  await context.sync()
  const items = paragraphs.items

  // Fast path: indexes in bounds and (when given) the anchor text still matches
  // there — the common case where nothing drifted.
  if (end < items.length) {
    if (!expectedStartText && !expectedEndText) return { items, start, end }
    items[start].load('text')
    items[end].load('text')
    await context.sync()
    const startOk = !expectedStartText || paragraphMatchesAnchor(items[start].text, expectedStartText)
    const endOk = !expectedEndText || paragraphMatchesAnchor(items[end].text, expectedEndText)
    if (startOk && endOk) return { items, start, end }
  }

  // The indexes are out of bounds or no longer point at the anchored text — the
  // document re-flowed or the indexes drifted (common with PDFs converted to
  // Word). Relocate by the anchor text so a still-present paragraph isn't
  // wrongly reported as stale; only a genuinely missing anchor is unresolvable.
  if (!expectedStartText && !expectedEndText) {
    throw new AnchorNotFoundError(
      `Paragraph range ${start}–${end} is out of bounds — the document has ${items.length} paragraphs. ` +
        'It may have changed since the proposal.'
    )
  }
  items.forEach((paragraph) => paragraph.load('text'))
  await context.sync()
  const texts = items.map((paragraph) => paragraph.text)

  const matchingIndexes = (expected: string | undefined, from = 0): number[] =>
    expected
      ? texts.flatMap((text, index) => (index >= from && paragraphMatchesAnchor(text, expected) ? [index] : []))
      : []
  const startMatches = matchingIndexes(expectedStartText)
  if (startMatches.length > 1) {
    throw new AnchorNotFoundError(
      'The proposed starting paragraph is not unique in the current document. Ask Ramp AI for a more specific proposal.'
    )
  }
  let newStart = startMatches[0] ?? -1
  const endMatches = matchingIndexes(expectedEndText, Math.max(0, newStart))
  if (endMatches.length > 1) {
    throw new AnchorNotFoundError(
      'The proposed ending paragraph is not unique in the current document. Ask Ramp AI for a more specific proposal.'
    )
  }
  let newEnd = endMatches[0] ?? -1
  // Derive the missing endpoint from the matched one, preserving the span length.
  if (newStart < 0 && newEnd >= 0) newStart = newEnd - (end - start)
  if (newEnd < 0 && newStart >= 0) newEnd = newStart + (end - start)
  if (newStart < 0 || newEnd < newStart || newEnd >= items.length) {
    throw new AnchorNotFoundError(
      'The proposed paragraphs changed since this proposal was created. ' +
        'Ask Ramp AI to review the latest document and create a fresh proposal.'
    )
  }
  return { items, start: newStart, end: newEnd }
}

/** True when a replacement payload carries no content — the proposal DELETES
 * the target instead of rewriting it (e.g. stripping placeholder paragraphs).
 * Word's insertHtml/insertText reject empty strings with InvalidArgument, so
 * deletions must go through Range/Paragraph delete instead. */
function isDeletion(html: string, plainText?: string): boolean {
  return !html.trim() && !(plainText ?? '').trim()
}

/** Delete whole paragraphs (marks included, so no blank line is left behind).
 * Table-cell paragraphs only clear their content — removing a cell paragraph's
 * mark would damage the cell. Returns a collapsed range at the deletion point. */
async function deleteParagraphRange(
  context: Word.RequestContext,
  resolved: ResolvedParagraphRange
): Promise<Word.Range> {
  const targets = resolved.items.slice(resolved.start, resolved.end + 1)
  targets.forEach((paragraph) => paragraph.load('tableNestingLevel'))
  await context.sync()
  // Anchor the returned range on a surviving neighbour, not the deleted text.
  const marker =
    resolved.start > 0
      ? resolved.items[resolved.start - 1].getRange(Word.RangeLocation.end)
      : context.document.body.getRange(Word.RangeLocation.start)
  for (const paragraph of targets) {
    if (paragraph.tableNestingLevel > 0) paragraph.getRange(Word.RangeLocation.content).delete()
    else paragraph.delete()
  }
  return marker
}

/** Resolve and replace a paragraph range. Shared by the single tool and the
 * batch apply; callers own the final sync. */
async function replaceParagraphRangeInContext(
  context: Word.RequestContext,
  start: number,
  end: number,
  html: string,
  expectedStartText?: string,
  expectedEndText?: string,
  plainText?: string
): Promise<Word.Range> {
  const resolved = await resolveParagraphRange(context, start, end, expectedStartText, expectedEndText)
  const first = resolved.items[resolved.start]
  await assertSafeParagraphRange(context, resolved)
  if (isDeletion(html, plainText)) return deleteParagraphRange(context, resolved)

  // A plain single-paragraph rewrite replaces content only, preserving the
  // paragraph mark and therefore its style, list membership, alignment, and
  // table-cell structure.
  // Never replace a single paragraph's mark. In a table that mark is tied to
  // the cell boundary; in body text it carries paragraph-level formatting.
  const target =
    resolved.start === resolved.end
      ? first.getRange(Word.RangeLocation.content)
      : paragraphSpanRange(resolved.items, resolved.start, resolved.end)
  return plainText !== undefined && resolved.start === resolved.end
    ? insertTextPreservingFont(context, target, plainText, expectedStartText)
    : insertHtmlInheritingFont(context, target, html, Word.InsertLocation.replace)
}

export async function replaceParagraphRange(
  start: number,
  end: number,
  html: string,
  expectedStartText?: string,
  expectedEndText?: string,
  plainText?: string
): Promise<void> {
  return Word.run(async (context) => {
    const inserted = await replaceParagraphRangeInContext(
      context,
      start,
      end,
      html,
      expectedStartText,
      expectedEndText,
      plainText
    )
    inserted.select()
    await context.sync()
  })
}

// Word's search API rejects longer strings.
const MAX_SEARCH_CHARS = 255

/**
 * Replace a span of existing text, anchored by exact snippets rather than the
 * live selection (which may have moved by apply time). Replaces from the start
 * of the first `find` match through the end of the first subsequent `findEnd`
 * match (or just the `find` match when `findEnd` is omitted).
 */
/**
 * Resolve the live document range an anchored replacement targets: from the
 * start of the first `find` match through the end of the first subsequent
 * `findEnd` match (or just the `find` match when `findEnd` is omitted). Shared
 * by the replace and scroll-to-target paths.
 */
const SEARCH_OPTIONS = { matchCase: true, ignoreSpace: false }

// Word's search rejects strings over MAX_SEARCH_CHARS, so anchors longer than
// this are reduced to a searchable head/tail (trimmed to a word boundary). Kept
// safely under the cap.
const ANCHOR_SEARCH_CHARS = 200

function boundedHead(text: string): string {
  if (text.length <= ANCHOR_SEARCH_CHARS) return text
  return text.slice(0, ANCHOR_SEARCH_CHARS).replace(/\s+\S*$/, '')
}

function boundedTail(text: string): string {
  if (text.length <= ANCHOR_SEARCH_CHARS) return text
  return text.slice(-ANCHOR_SEARCH_CHARS).replace(/^\S*\s+/, '')
}

async function resolveTextRange(
  context: Word.RequestContext,
  find: string,
  findEnd?: string,
  paragraphIndex?: number,
  expectedParagraphText?: string,
  occurrence = 0,
  findEndOccurrence = 0
): Promise<Word.Range> {
  if (!find.trim()) throw new Error('The text anchor to replace cannot be empty.')
  const body = context.document.body
  let searchRegion: Word.Range = body.getRange(Word.RangeLocation.whole)
  if (paragraphIndex !== undefined) {
    const resolved = await resolveParagraphRange(
      context,
      paragraphIndex,
      paragraphIndex,
      expectedParagraphText,
      expectedParagraphText
    )
    searchRegion = resolved.items[resolved.start].getRange(Word.RangeLocation.content)
  }
  const startMatches = searchRegion.search(boundedHead(find), SEARCH_OPTIONS)
  startMatches.load('items')
  await context.sync()
  if (startMatches.items.length === 0) {
    throw new AnchorNotFoundError('Could not find the text to replace — it may have changed since the proposal.')
  }
  // Legacy proposals did not identify a paragraph/occurrence, so multiple
  // document matches are genuinely ambiguous. New proposals are deterministic:
  // select the requested zero-based occurrence inside the verified paragraph.
  if (paragraphIndex === undefined && startMatches.items.length > 1) {
    throw new AmbiguousWordAnchorError(
      'The text to replace occurs more than once in the document. ' +
        'Ask Ramp AI to create a paragraph-scoped proposal.'
    )
  }
  if (!Number.isInteger(occurrence) || occurrence < 0 || occurrence >= startMatches.items.length) {
    throw new AnchorNotFoundError(
      `Occurrence ${occurrence} of the text to replace no longer exists in the target paragraph.`
    )
  }

  const startRange = startMatches.items[occurrence].getRange(Word.RangeLocation.whole)
  const containingParagraph = startRange.paragraphs.getFirst().getRange(Word.RangeLocation.content)
  let target = startRange

  // The range ends at the end of: an explicit `findEnd`, or — when `find` is too
  // long for one search — the tail of `find` itself (so the whole span is still
  // replaced, not just its head).
  let endSearch: string | undefined
  let endRegion: Word.Range | undefined
  if (findEnd) {
    // Keep anchored replacements inside one paragraph. Crossing a paragraph or
    // table-cell boundary is where Word range replacement becomes destructive.
    endSearch = boundedTail(findEnd)
    endRegion = startRange.getRange(Word.RangeLocation.after).expandTo(containingParagraph)
  } else if (find.length > ANCHOR_SEARCH_CHARS) {
    // Include the start match: the tail may overlap the head for mid-length finds.
    endSearch = boundedTail(find)
    endRegion = startRange.expandTo(containingParagraph)
  }

  if (endSearch && endRegion) {
    const endMatches = endRegion.search(endSearch, SEARCH_OPTIONS)
    endMatches.load('items')
    await context.sync()
    if (endMatches.items.length === 0) {
      throw new AnchorNotFoundError(
        'Could not find the end of the text to replace — it may have changed since the proposal.'
      )
    }
    if (!Number.isInteger(findEndOccurrence) || findEndOccurrence < 0 || findEndOccurrence >= endMatches.items.length) {
      throw new AnchorNotFoundError(
        `Occurrence ${findEndOccurrence} of the ending text no longer exists after the start anchor.`
      )
    }
    target = startRange.expandTo(endMatches.items[findEndOccurrence].getRange(Word.RangeLocation.end))
  }
  return target
}

export async function replaceTextRangeWithHtml(
  find: string,
  html: string,
  findEnd?: string,
  plainText?: string,
  paragraphIndex?: number,
  expectedParagraphText?: string,
  occurrence = 0,
  findEndOccurrence = 0
): Promise<void> {
  return Word.run(async (context) => {
    const target = await resolveTextRange(
      context,
      find,
      findEnd,
      paragraphIndex,
      expectedParagraphText,
      occurrence,
      findEndOccurrence
    )

    // Plain replacements use insertText so the span's character formatting
    // (bold, font, color) survives; the helper additionally restores the label's
    // font size when the span starts with a differently-sized leading character
    // (e.g. an anomalous leading tab). HTML replacements inherit the span's base
    // font so they don't reset to Word's HTML defaults.
    const inserted = await replaceTextTarget(context, target, html, plainText, find)
    // Select the new content so Word scrolls the edit into view.
    inserted.select()
    await context.sync()
  })
}

/** Replace (or, for empty content, delete) an anchored text span. The span's
 * containing paragraph survives a deletion, so its start anchors the result. */
async function replaceTextTarget(
  context: Word.RequestContext,
  target: Word.Range,
  html: string,
  plainText: string | undefined,
  find: string
): Promise<Word.Range> {
  if (isDeletion(html, plainText)) {
    const marker = target.getRange(Word.RangeLocation.start)
    target.delete()
    return marker
  }
  return plainText !== undefined
    ? insertTextPreservingFont(context, target, plainText, find)
    : insertHtmlInheritingFont(context, target, html, Word.InsertLocation.replace)
}

/* ------------------------------ Batch apply ------------------------------- */

/** One edit inside `applyEditBatch`. `html`/`plainText` are pre-converted by
 * the caller (same as the single-tool handlers). */
export type WordBatchEdit =
  | {
      kind: 'replaceText'
      find: string
      findEnd?: string
      paragraphIndex: number
      expectedParagraphText: string
      occurrence: number
      findEndOccurrence: number
      html: string
      plainText?: string
    }
  | {
      kind: 'replaceParagraphs'
      start: number
      end: number
      expectedStartText: string
      expectedEndText: string
      html: string
      plainText?: string
    }
  | { kind: 'insertBefore'; paragraphIndex: number; expectedParagraphText: string; html: string }
  | { kind: 'append'; html: string }

export interface BatchApplyResult {
  applied: number
  /** False when the host lacks change tracking (WordApi 1.4) and edits landed untracked. */
  tracked: boolean
}

/** The paragraph span an edit occupies, for ordering and overlap checks.
 * Appends sit past every paragraph, so they sort first under bottom-up order. */
interface EditSpan {
  start: number
  end: number
}

async function resolveBatchEditSpan(context: Word.RequestContext, edit: WordBatchEdit): Promise<EditSpan> {
  if (edit.kind === 'append') return { start: Number.POSITIVE_INFINITY, end: Number.POSITIVE_INFINITY }
  if (edit.kind === 'insertBefore') {
    const resolved = await resolveParagraphRange(
      context,
      edit.paragraphIndex,
      edit.paragraphIndex,
      edit.expectedParagraphText,
      edit.expectedParagraphText
    )
    return { start: resolved.start, end: resolved.start }
  }
  if (edit.kind === 'replaceText') {
    const resolved = await resolveParagraphRange(
      context,
      edit.paragraphIndex,
      edit.paragraphIndex,
      edit.expectedParagraphText,
      edit.expectedParagraphText
    )
    // Also resolves the find/occurrence anchors so a bad span fails validation.
    await resolveTextRange(
      context,
      edit.find,
      edit.findEnd,
      edit.paragraphIndex,
      edit.expectedParagraphText,
      edit.occurrence,
      edit.findEndOccurrence
    )
    return { start: resolved.start, end: resolved.start }
  }
  const resolved = await resolveParagraphRange(context, edit.start, edit.end, edit.expectedStartText, edit.expectedEndText)
  await assertSafeParagraphRange(context, resolved)
  return { start: resolved.start, end: resolved.end }
}

async function applyBatchEdit(context: Word.RequestContext, edit: WordBatchEdit): Promise<Word.Range> {
  if (edit.kind === 'append') return appendHtmlWithBaseFont(context, edit.html)
  if (edit.kind === 'insertBefore') {
    const resolved = await resolveParagraphRange(
      context,
      edit.paragraphIndex,
      edit.paragraphIndex,
      edit.expectedParagraphText,
      edit.expectedParagraphText
    )
    // A collapsed range at the paragraph's start both anchors the insertion and
    // supplies the base font the new content inherits.
    const anchor = resolved.items[resolved.start].getRange(Word.RangeLocation.start)
    return insertHtmlInheritingFont(context, anchor, edit.html, Word.InsertLocation.before)
  }
  if (edit.kind === 'replaceText') {
    const target = await resolveTextRange(
      context,
      edit.find,
      edit.findEnd,
      edit.paragraphIndex,
      edit.expectedParagraphText,
      edit.occurrence,
      edit.findEndOccurrence
    )
    return replaceTextTarget(context, target, edit.html, edit.plainText, edit.find)
  }
  return replaceParagraphRangeInContext(
    context,
    edit.start,
    edit.end,
    edit.html,
    edit.expectedStartText,
    edit.expectedEndText,
    edit.plainText
  )
}

function batchEditError(error: unknown, index: number, total: number, applied: number): Error {
  const detail = error instanceof Error ? error.message : String(error)
  const partial = applied > 0 ? ` ${applied} earlier edit(s) were already applied; review the document.` : ''
  const wrapped = new Error(`Edit ${index + 1} of ${total} failed: ${detail}${partial}`)
  // A failure AFTER some edits landed must not be retried verbatim — the card
  // switches to the fresh-proposal flow when it sees this name.
  wrapped.name = applied > 0 ? 'PartialBatchError' : error instanceof Error ? error.name : wrapped.name
  return wrapped
}

/**
 * Apply several edits to the document as one transaction:
 *
 *  1. VALIDATE — every anchor is resolved against the live document (same
 *     checks as the single tools) and edits may not touch overlapping
 *     paragraphs; any failure aborts before anything is modified.
 *  2. APPLY — edits run bottom-up in document order (appends first, then
 *     anchored edits from the highest paragraph index to the lowest), so an
 *     applied edit never shifts the anchors of the ones still pending. Each
 *     edit re-resolves at apply time, exactly like its single-tool equivalent.
 *
 * With `asTrackedChanges` (and WordApi 1.4), the batch runs under Word's
 * Track Changes: every edit lands as a tracked revision the user accepts or
 * rejects natively, and the document's previous tracking mode is restored
 * afterwards. On hosts without change tracking the batch applies untracked and
 * the result says so.
 */
export async function applyEditBatch(edits: WordBatchEdit[], asTrackedChanges: boolean): Promise<BatchApplyResult> {
  if (edits.length === 0) throw new Error('This batch contains no edits.')
  return Word.run(async (context) => {
    const spans: EditSpan[] = []
    for (const [index, edit] of edits.entries()) {
      try {
        spans.push(await resolveBatchEditSpan(context, edit))
      } catch (error) {
        throw batchEditError(error, index, edits.length, 0)
      }
    }
    for (let a = 0; a < spans.length; a += 1) {
      for (let b = a + 1; b < spans.length; b += 1) {
        if (edits[a].kind === 'append' || edits[b].kind === 'append') continue
        if (spans[a].start <= spans[b].end && spans[b].start <= spans[a].end) {
          throw new Error(
            `Edits ${a + 1} and ${b + 1} target overlapping paragraphs and cannot be applied together. ` +
              'Ask Ramp AI to combine them into one edit.'
          )
        }
      }
    }

    // Bottom-up: appends (Infinity) first, then anchored edits by descending
    // paragraph index. Ties (several appends) keep their proposed order.
    const order = edits
      .map((_, index) => index)
      .sort((a, b) => {
        // Equal positions (several appends at Infinity) keep their proposed order.
        if (spans[a].start === spans[b].start) return a - b
        return spans[b].start > spans[a].start ? 1 : -1
      })

    const doc = context.document
    // Office.js reads the mode back as enum-or-string; keep whichever we got.
    let restoreMode: Word.ChangeTrackingMode | 'Off' | 'TrackAll' | 'TrackMineOnly' | undefined
    let tracked = false
    if (asTrackedChanges && Office.context.requirements.isSetSupported('WordApi', '1.4')) {
      doc.load('changeTrackingMode')
      await context.sync()
      if (doc.changeTrackingMode !== Word.ChangeTrackingMode.trackAll) {
        restoreMode = doc.changeTrackingMode
        doc.changeTrackingMode = Word.ChangeTrackingMode.trackAll
        await context.sync()
      }
      tracked = true
    }

    let applied = 0
    let topmost: Word.Range | undefined
    try {
      for (const index of order) {
        try {
          topmost = await applyBatchEdit(context, edits[index])
          applied += 1
        } catch (error) {
          // Validation makes this unlikely; if Word still rejects an edit, say
          // exactly how far the batch got instead of pretending all-or-nothing.
          throw batchEditError(error, index, edits.length, applied)
        }
      }
    } finally {
      // The tracking mode only affects FUTURE edits — the revisions created
      // above survive the restore.
      if (restoreMode !== undefined) {
        doc.changeTrackingMode = restoreMode
        await context.sync()
      }
    }
    // The last applied edit is the topmost one; select it so Word scrolls there.
    topmost?.select()
    await context.sync()
    return { applied, tracked }
  })
}

/**
 * Read-only batch validation, mirroring `applyEditBatch`'s validate phase.
 * Returns true when every anchor still resolves against the live document.
 */
export async function canResolveEditBatch(edits: WordBatchEdit[]): Promise<boolean> {
  if (edits.length === 0) return true
  return resolvesAgainstDocument(async (context) => {
    for (const edit of edits) await resolveBatchEditSpan(context, edit)
  })
}

/**
 * Reveal where a proposal would land WITHOUT changing the document: select the
 * existing target span so Word scrolls it into view. Mirrors the anchoring of
 * `replaceTextRangeWithHtml` so the previewed location matches the apply.
 */
export async function scrollToTextRange(
  find: string,
  findEnd?: string,
  paragraphIndex?: number,
  expectedParagraphText?: string,
  occurrence = 0,
  findEndOccurrence = 0
): Promise<void> {
  return Word.run(async (context) => {
    const target = await resolveTextRange(
      context,
      find,
      findEnd,
      paragraphIndex,
      expectedParagraphText,
      occurrence,
      findEndOccurrence
    )
    target.select()
    await context.sync()
  })
}

/** Reveal a paragraph range (zero-based, inclusive) by selecting it. */
export async function scrollToParagraphRange(start: number, end: number): Promise<void> {
  return Word.run(async (context) => {
    const resolved = await resolveParagraphRange(context, start, end)
    paragraphSpanRange(resolved.items, resolved.start, resolved.end).select()
    await context.sync()
  })
}

/** Reveal the current selection by re-selecting it (scrolls it back into view). */
export async function scrollToSelection(): Promise<void> {
  return Word.run(async (context) => {
    context.document.getSelection().select()
    await context.sync()
  })
}

/** Reveal the end of the document (where an append would land). */
export async function scrollToDocumentEnd(): Promise<void> {
  return Word.run(async (context) => {
    context.document.body.getRange(Word.RangeLocation.end).select()
    await context.sync()
  })
}

/* ------------------------- Read-only apply checks ------------------------- */

/**
 * Run a resolve against the live document and report whether its anchor still
 * matches — WITHOUT modifying anything. A missing anchor (`AnchorNotFoundError`)
 * means the proposal is stale; any other failure is unexpected and propagates so
 * the caller can keep the proposal actionable rather than wrongly hiding it.
 */
async function resolvesAgainstDocument(
  resolve: (context: Word.RequestContext) => Promise<unknown>
): Promise<boolean> {
  return Word.run(async (context) => {
    try {
      await resolve(context)
      return true
    } catch (error) {
      if (error instanceof AnchorNotFoundError) return false
      throw error
    }
  })
}

/** Whether `replaceTextRangeWithHtml` would still find its anchored span. */
export async function canResolveTextRange(
  find: string,
  findEnd?: string,
  paragraphIndex?: number,
  expectedParagraphText?: string,
  occurrence = 0,
  findEndOccurrence = 0
): Promise<boolean> {
  return resolvesAgainstDocument((context) =>
    resolveTextRange(
      context,
      find,
      findEnd,
      paragraphIndex,
      expectedParagraphText,
      occurrence,
      findEndOccurrence
    )
  )
}

/** Whether `replaceParagraphRange` would still resolve (bounds + anchor text). */
export async function canResolveParagraphRange(
  start: number,
  end: number,
  expectedStartText?: string,
  expectedEndText?: string
): Promise<boolean> {
  return resolvesAgainstDocument(async (context) => {
    const resolved = await resolveParagraphRange(context, start, end, expectedStartText, expectedEndText)
    await assertSafeParagraphRange(context, resolved)
  })
}

/** Whether the live selection still matches `replaceSelectionWithHtml`'s expectation. */
export async function canResolveSelection(expectedSelectionText: string, replacement = true): Promise<boolean> {
  return resolvesAgainstDocument(async (context) => {
    const selection = context.document.getSelection()
    selection.load('text')
    await context.sync()
    assertExpectedText(selection.text, expectedSelectionText, 'selected text')
    if (replacement) await assertRangeDoesNotCrossTableParagraphs(context, selection)
  })
}

/* --------------------------- Document change events ----------------------- */

/** Office.js event registrations expose just enough to detach them later. */
interface RemovableEvent {
  context: OfficeExtension.ClientRequestContext
  remove(): void
}

// Paragraph events fire per edit; coalesce bursts (e.g. typing) into one
// re-validation so each proposal card runs at most one check per quiet window.
const DOCUMENT_CHANGE_DEBOUNCE_MS = 400

const documentChangeListeners = new Set<() => void>()
let activeChangeEvents: RemovableEvent[] | null = null
let subscribingChangeEvents = false
let notifyTimer: ReturnType<typeof setTimeout> | null = null

function scheduleDocumentChangeNotify(): void {
  if (notifyTimer) clearTimeout(notifyTimer)
  notifyTimer = setTimeout(() => {
    notifyTimer = null
    documentChangeListeners.forEach((listener) => listener())
  }, DOCUMENT_CHANGE_DEBOUNCE_MS)
}

async function handleParagraphEvent(): Promise<void> {
  scheduleDocumentChangeNotify()
}

async function subscribeDocumentChangeEvents(): Promise<void> {
  if (activeChangeEvents || subscribingChangeEvents) return
  subscribingChangeEvents = true
  try {
    await Word.run(async (context) => {
      const doc = context.document
      const events: RemovableEvent[] = [
        doc.onParagraphAdded.add(handleParagraphEvent),
        doc.onParagraphChanged.add(handleParagraphEvent),
        doc.onParagraphDeleted.add(handleParagraphEvent)
      ]
      await context.sync()
      activeChangeEvents = events
    })
  } catch {
    // Paragraph events need WordApi 1.5; on older hosts they never register and
    // subscribers fall back to validating on click. Non-fatal.
  } finally {
    subscribingChangeEvents = false
  }
}

async function unsubscribeDocumentChangeEvents(): Promise<void> {
  const events = activeChangeEvents
  activeChangeEvents = null
  if (!events?.length) return
  try {
    events.forEach((event) => event.remove())
    await events[0].context.sync()
  } catch {
    // Best effort — listeners are already gone, so stray events are harmless.
  }
}

/**
 * Invoke `listener` (debounced) whenever the Word document body changes, so
 * cached proposals can be re-validated against the live document. The underlying
 * Word events are registered once and shared across all listeners, then torn
 * down when the last listener unsubscribes. Returns that unsubscribe function.
 */
export function onDocumentChanged(listener: () => void): () => void {
  documentChangeListeners.add(listener)
  void subscribeDocumentChangeEvents()
  return () => {
    documentChangeListeners.delete(listener)
    if (documentChangeListeners.size === 0) void unsubscribeDocumentChangeEvents()
  }
}

/**
 * Locate arbitrary content already in the document and select it (scrolls into
 * view). Used to reveal an *applied* proposal, whose original anchor no longer
 * exists — we search for the inserted text instead. Returns false when nothing
 * matched. Shares the scan finder's tolerant, segment-based search.
 */
export async function scrollToContent(text: string): Promise<boolean> {
  return highlightScanMatch(text)
}

/**
 * Scan-result location. Clicking a checklist-scan finding (a verbatim
 * `textBlock` the model located in the document) selects the matching span in
 * the live Word document — Word's native selection both highlights it and
 * scrolls it into view, so no font tint is applied (the highlighter palette
 * can't render the light colors we'd want anyway). Matching is content-based
 * (search), like `replaceTextRangeWithHtml`, so it survives benign edits.
 */
let hasActiveSelection = false

/**
 * Word's search engine matches text Word-internally renders as a single run,
 * but typographic substitutions (smart quotes, en/em dashes, ellipses,
 * non-breaking spaces) differ between the model's plain-text block and the
 * document. Fold them to their ASCII forms and collapse whitespace so the
 * needle lines up; `ignoreSpace`/`ignorePunct` cover the remaining slack.
 */
function normalizeForSearch(text: string): string {
  return text
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[‐-―]/g, '-')
    .replace(/…/g, '...')
    .replace(/[   ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const SCAN_SEARCH_OPTIONS = { matchCase: false, ignoreSpace: true, ignorePunct: true }

// Anchors shorter than this match too loosely (a common phrase appears many
// times), so we don't trust them to locate or to bound a selection.
const MIN_SEGMENT_WORDS = 4

/**
 * Break a scan block into sentence-sized search anchors.
 *
 * Word's `search` can't match across paragraph marks or table-cell boundaries,
 * so a block the model assembled from several paragraphs/cells — common in
 * structured docs with headings, tables, and block quotes — never matches as a
 * whole. Worse, leading slices of the block fail too whenever the block opens
 * with a heading on its own line (the slice always straddles that first
 * boundary). Sentence-sized pieces, by contrast, each live inside a single
 * paragraph/cell — the granularity Word can actually match — so we anchor on
 * those and let the caller stitch the matched pieces back into one selection.
 */
function buildSearchSegments(text: string): string[] {
  const normalized = normalizeForSearch(text)
  if (!normalized) return []
  const segments: string[] = []
  const seen = new Set<string>()
  const push = (raw: string) => {
    let segment = raw.trim()
    if (segment.split(' ').length < MIN_SEGMENT_WORDS) return
    if (segment.length > MAX_SEARCH_CHARS) segment = segment.slice(0, MAX_SEARCH_CHARS).replace(/\s+\S*$/, '')
    if (segment && !seen.has(segment)) {
      seen.add(segment)
      segments.push(segment)
    }
  }
  for (const sentence of normalized.split(/(?<=[.;:!?])\s+/)) push(sentence)
  // Short findings have no sentence long enough to anchor on; fall back to the
  // whole (bounded) block so single-line matches still work.
  if (segments.length === 0) {
    const whole =
      normalized.length > MAX_SEARCH_CHARS ? normalized.slice(0, MAX_SEARCH_CHARS).replace(/\s+\S*$/, '') : normalized
    if (whole) segments.push(whole)
  }
  return segments
}

/** Search every segment once; return the first match of each, in block order. */
async function findSegmentMatches(context: Word.RequestContext, segments: string[]): Promise<Word.Range[]> {
  const body = context.document.body
  const searches = segments.map((segment) => body.search(segment, SCAN_SEARCH_OPTIONS))
  searches.forEach((result) => result.load('items'))
  await context.sync()
  const ranges: Word.Range[] = []
  for (const result of searches) {
    if (result.items.length > 0) ranges.push(result.items[0])
  }
  return ranges
}

/**
 * Select the contiguous document span the matched segments cover: anchor on the
 * first matched segment, then union in every later segment that sits after it
 * in document order. This pulls the whole multi-paragraph/table finding into one
 * selection while ignoring a stray segment that happens to match earlier in the
 * document. Needs WordApi 1.3 range comparison; the caller falls back when that
 * is unavailable.
 */
async function selectMatchedSpan(segments: string[]): Promise<boolean> {
  return Word.run(async (context) => {
    const ranges = await findSegmentMatches(context, segments)
    if (ranges.length === 0) return false

    const anchor = ranges[0]
    const rest = ranges.slice(1)
    const relations = rest.map((range) => anchor.compareLocationWith(range))
    if (relations.length > 0) await context.sync()

    let span = anchor
    rest.forEach((range, index) => {
      const relation = relations[index].value
      if (relation === Word.LocationRelation.before || relation === Word.LocationRelation.adjacentBefore) {
        span = span.expandTo(range)
      }
    })
    span.select()
    await context.sync()
    hasActiveSelection = true
    return true
  })
}

/** Fallback: just select the first segment that matches (no span stitching). */
async function selectFirstMatch(segments: string[]): Promise<boolean> {
  return Word.run(async (context) => {
    const ranges = await findSegmentMatches(context, segments)
    if (ranges.length === 0) return false
    ranges[0].select()
    await context.sync()
    hasActiveSelection = true
    return true
  })
}

/**
 * Find the scan block in the document and select it (scrolls it into view).
 * Returns false when nothing matched (the caller surfaces a "couldn't locate"
 * hint). Selecting a new match replaces any previous selection automatically.
 */
export async function highlightScanMatch(text: string): Promise<boolean> {
  const segments = buildSearchSegments(text)
  if (segments.length === 0) return false
  try {
    return await selectMatchedSpan(segments)
  } catch {
    // Range comparison (WordApi 1.3) is unavailable or errored — still locate
    // the finding by selecting its first matchable sentence.
    return selectFirstMatch(segments)
  }
}

/**
 * Clear the scan selection (e.g. on toggle-off or when leaving the scan view)
 * by collapsing it to its start, so the matched text is no longer selected.
 */
export async function clearScanHighlight(): Promise<void> {
  if (!hasActiveSelection) return
  hasActiveSelection = false
  await Word.run(async (context) => {
    context.document.getSelection().getRange(Word.RangeLocation.start).select()
    await context.sync()
  })
}
